import { useState, useEffect, useRef } from 'react';
import AdminLayout from './AdminLayout';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api/v1';

type Popup = {
    _id: string;
    id: string;
    name: string;
    image: {
        url: string;
        publicId?: string;
    };
    linkUrl: string;
    linkTarget: '_self' | '_blank';
    isActive: boolean;
    showOnce: boolean;
    startDate?: string;
    endDate?: string;
    priority: number;
    createdAt: string;
    updatedAt: string;
};

const PopupsPage = () => {
    const [popups, setPopups] = useState<Popup[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingPopup, setEditingPopup] = useState<Popup | null>(null);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        linkUrl: '',
        linkTarget: '_blank' as '_self' | '_blank',
        isActive: true,
        showOnce: true,
        startDate: '',
        endDate: '',
        priority: 0
    });
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    useEffect(() => {
        fetchPopups();
    }, []);

    const getAuthHeaders = () => {
        const token = localStorage.getItem('auth_token');
        return {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        };
    };

    const getAuthHeadersFormData = () => {
        const token = localStorage.getItem('auth_token');
        return {
            'Authorization': `Bearer ${token}`
        };
    };

    const fetchPopups = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/popups`, {
                headers: getAuthHeaders()
            });
            const data = await res.json();
            if (data.success) {
                setPopups(data.data);
            }
        } catch (err) {
            console.error('Error fetching popups:', err);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            linkUrl: '',
            linkTarget: '_blank',
            isActive: true,
            showOnce: true,
            startDate: '',
            endDate: '',
            priority: 0
        });
        setEditingPopup(null);
        setError(null);
        setImagePreview(null);
        setSelectedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleEdit = (popup: Popup) => {
        setEditingPopup(popup);
        setFormData({
            name: popup.name,
            linkUrl: popup.linkUrl,
            linkTarget: popup.linkTarget || '_blank',
            isActive: popup.isActive,
            showOnce: popup.showOnce ?? true,
            startDate: popup.startDate ? popup.startDate.split('T')[0] : '',
            endDate: popup.endDate ? popup.endDate.split('T')[0] : '',
            priority: popup.priority || 0
        });
        setImagePreview(popup.image?.url || null);
        setSelectedFile(null);
        setShowForm(true);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                setError('Por favor selecciona un archivo de imagen');
                return;
            }
            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                setError('La imagen no puede superar los 5MB');
                return;
            }
            setSelectedFile(file);
            setImagePreview(URL.createObjectURL(file));
            setError(null);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setError(null);

        try {
            // If creating new and no image selected
            if (!editingPopup && !selectedFile) {
                setError('Por favor selecciona una imagen para el popup');
                setSaving(false);
                return;
            }

            const payload: Record<string, unknown> = {
                ...formData,
                startDate: formData.startDate || null,
                endDate: formData.endDate || null
            };

            // Only include image if editing and it exists
            if (editingPopup?.image?.url) {
                payload.image = editingPopup.image;
            }

            // Create or update popup
            const url = editingPopup
                ? `${API_URL}/popups/${editingPopup._id}`
                : `${API_URL}/popups`;

            const res = await fetch(url, {
                method: editingPopup ? 'PUT' : 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(payload)
            });

            const data = await res.json();

            if (!data.success) {
                setError(data.error?.message || 'Error al guardar el popup');
                setSaving(false);
                return;
            }

            // If we have a new image to upload
            if (selectedFile) {
                setUploading(true);
                const popupId = data.data._id || data.data.id;
                const formDataFile = new FormData();
                formDataFile.append('file', selectedFile);

                const uploadRes = await fetch(`${API_URL}/popups/${popupId}/image`, {
                    method: 'POST',
                    headers: getAuthHeadersFormData(),
                    body: formDataFile
                });

                const uploadData = await uploadRes.json();
                setUploading(false);

                if (!uploadData.success) {
                    setError('Popup creado pero error al subir la imagen: ' + (uploadData.error?.message || 'Error desconocido'));
                    await fetchPopups();
                    setSaving(false);
                    return;
                }
            }

            await fetchPopups();
            setShowForm(false);
            resetForm();
        } catch (err) {
            setError('Error de conexión');
        } finally {
            setSaving(false);
            setUploading(false);
        }
    };

    const handleToggle = async (popup: Popup) => {
        try {
            const res = await fetch(`${API_URL}/popups/${popup._id}/toggle`, {
                method: 'PATCH',
                headers: getAuthHeaders()
            });

            if (res.ok) {
                await fetchPopups();
            }
        } catch (err) {
            console.error('Error toggling popup:', err);
        }
    };

    const handleDelete = async (popup: Popup) => {
        if (!confirm(`¿Estás seguro de eliminar el popup "${popup.name}"?`)) {
            return;
        }

        try {
            const res = await fetch(`${API_URL}/popups/${popup._id}`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });

            if (res.ok) {
                await fetchPopups();
            }
        } catch (err) {
            console.error('Error deleting popup:', err);
        }
    };

    const formatDate = (date: string | undefined) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('es-ES');
    };

    return (
        <AdminLayout title="Popups">
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <p className="text-gray-600">
                        Gestiona los popups que se muestran a los usuarios al entrar en la tienda.
                    </p>
                    <button
                        onClick={() => { resetForm(); setShowForm(true); }}
                        className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
                    >
                        + Nuevo popup
                    </button>
                </div>

                {/* Form Modal */}
                {showForm && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                            <div className="p-6">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl font-semibold">
                                        {editingPopup ? 'Editar popup' : 'Nuevo popup'}
                                    </h2>
                                    <button
                                        onClick={() => { setShowForm(false); resetForm(); }}
                                        className="text-gray-400 hover:text-gray-600"
                                    >
                                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>

                                {error && (
                                    <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
                                        {error}
                                    </div>
                                )}

                                <form onSubmit={handleSubmit} className="space-y-4">
                                    {/* Name */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Nombre *
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.name}
                                            onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                                            placeholder="Ej: Promo Navidad"
                                        />
                                    </div>

                                    {/* Image Upload */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Imagen *
                                        </label>
                                        <div className="mt-1 flex items-center gap-4">
                                            <div className="flex-shrink-0">
                                                {imagePreview ? (
                                                    <img
                                                        src={imagePreview}
                                                        alt="Preview"
                                                        className="h-32 w-32 object-cover rounded-lg border border-gray-200"
                                                    />
                                                ) : (
                                                    <div className="h-32 w-32 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
                                                        <svg className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                        </svg>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex-1">
                                                <input
                                                    ref={fileInputRef}
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handleFileChange}
                                                    className="hidden"
                                                    id="popup-image"
                                                />
                                                <label
                                                    htmlFor="popup-image"
                                                    className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                                                >
                                                    {imagePreview ? 'Cambiar imagen' : 'Seleccionar imagen'}
                                                </label>
                                                <p className="mt-2 text-xs text-gray-500">
                                                    PNG, JPG, GIF hasta 5MB. Recomendado: 800x800px
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Link URL */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            URL de destino *
                                        </label>
                                        <input
                                            type="url"
                                            required
                                            value={formData.linkUrl}
                                            onChange={e => setFormData(prev => ({ ...prev, linkUrl: e.target.value }))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                                            placeholder="https://ejemplo.com/promo"
                                        />
                                    </div>

                                    {/* Link Target */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Abrir enlace en
                                        </label>
                                        <select
                                            value={formData.linkTarget}
                                            onChange={e => setFormData(prev => ({ ...prev, linkTarget: e.target.value as '_self' | '_blank' }))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                                        >
                                            <option value="_blank">Nueva pestaña</option>
                                            <option value="_self">Misma pestaña</option>
                                        </select>
                                    </div>

                                    {/* Dates */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Fecha inicio (opcional)
                                            </label>
                                            <input
                                                type="date"
                                                value={formData.startDate}
                                                onChange={e => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Fecha fin (opcional)
                                            </label>
                                            <input
                                                type="date"
                                                value={formData.endDate}
                                                onChange={e => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                                            />
                                        </div>
                                    </div>

                                    {/* Priority */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Prioridad
                                        </label>
                                        <input
                                            type="number"
                                            value={formData.priority}
                                            onChange={e => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                                            placeholder="0"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">Mayor prioridad = se muestra primero</p>
                                    </div>

                                    {/* Options */}
                                    <div className="space-y-3">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={formData.showOnce}
                                                onChange={e => setFormData(prev => ({ ...prev, showOnce: e.target.checked }))}
                                                className="w-5 h-5 rounded border-gray-300"
                                            />
                                            <span className="text-sm font-medium">Mostrar solo una vez por usuario</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={formData.isActive}
                                                onChange={e => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                                                className="w-5 h-5 rounded border-gray-300"
                                            />
                                            <span className="text-sm font-medium">Popup activo</span>
                                        </label>
                                    </div>

                                    {/* Submit */}
                                    <div className="flex justify-end gap-3 pt-4 border-t">
                                        <button
                                            type="button"
                                            onClick={() => { setShowForm(false); resetForm(); }}
                                            className="px-4 py-2 text-gray-700 hover:text-gray-900"
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={saving || uploading}
                                            className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 disabled:opacity-50"
                                        >
                                            {uploading ? 'Subiendo imagen...' : saving ? 'Guardando...' : (editingPopup ? 'Guardar cambios' : 'Crear popup')}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* Popups List */}
                {loading ? (
                    <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
                        <p className="mt-4 text-gray-600">Cargando popups...</p>
                    </div>
                ) : popups.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <h3 className="mt-2 text-sm font-semibold text-gray-900">No hay popups</h3>
                        <p className="mt-1 text-sm text-gray-500">Crea tu primer popup para mostrar a los usuarios.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {popups.map(popup => (
                            <div
                                key={popup._id}
                                className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
                            >
                                {/* Image */}
                                <div className="aspect-square bg-gray-100 relative">
                                    {popup.image?.url ? (
                                        <img
                                            src={popup.image.url}
                                            alt={popup.name}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <svg className="h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                        </div>
                                    )}
                                    {/* Status badge */}
                                    <div className="absolute top-2 right-2">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${popup.isActive
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-gray-100 text-gray-800'
                                            }`}>
                                            {popup.isActive ? 'Activo' : 'Inactivo'}
                                        </span>
                                    </div>
                                </div>

                                {/* Info */}
                                <div className="p-4">
                                    <h3 className="font-semibold text-gray-900 mb-1">{popup.name}</h3>
                                    <p className="text-sm text-gray-500 truncate mb-2">
                                        {popup.linkUrl}
                                    </p>
                                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                                        <span>
                                            {popup.startDate || popup.endDate
                                                ? `${formatDate(popup.startDate)} - ${formatDate(popup.endDate)}`
                                                : 'Sin límite'}
                                        </span>
                                        {popup.showOnce && (
                                            <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                                                Solo una vez
                                            </span>
                                        )}
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                                        <button
                                            onClick={() => handleToggle(popup)}
                                            className={`text-sm font-medium ${popup.isActive ? 'text-orange-600 hover:text-orange-700' : 'text-green-600 hover:text-green-700'}`}
                                        >
                                            {popup.isActive ? 'Desactivar' : 'Activar'}
                                        </button>
                                        <div className="flex items-center gap-3">
                                            <button
                                                onClick={() => handleEdit(popup)}
                                                className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
                                            >
                                                Editar
                                            </button>
                                            <button
                                                onClick={() => handleDelete(popup)}
                                                className="text-sm font-medium text-red-600 hover:text-red-700"
                                            >
                                                Eliminar
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};

export default PopupsPage;
